import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import { serviceDetails, getServiceDetail } from "@/mocks/services";
import { services, visionMission } from "@/mocks/home";
import { areaAnchorId } from "@/utils/areaSlug";
import { HERO_IMAGES } from "@/config/heroImages";

const extraFacilities = [
  {
    icon: "ri-store-3-line",
    title: "Warehouse Facility",
    text: "Secure, pest controlled and water-proof storage bays at our Bangalore hub for household and commercial goods.",
    href: "/warehouse",
  },
  {
    icon: "ri-box-3-line",
    title: "Packing & Unpacking",
    text: "Multi-layer wrapping for fragile items, cartons, foam and wooden crates using our own in-house material.",
    href: "/services/house-shifting",
  },
  {
    icon: "ri-roadster-line",
    title: "Intercity Shifting",
    text: "Scheduled routes from Bangalore to Mysore and Tumkur with dedicated covered-body vehicles.",
    href: "/services/office-shifting",
  },
];

export default function ServicesIndexPage() {
  const localityAreas = getServiceDetail("local-shifting")?.areas ?? [];

  return (
    <>
      <PageHero
        eyebrow="Our Services"
        title="Moving & Shifting Services in Bangalore"
        subtitle="From a single item to a full household or an entire office floor, Lahari Packers and Movers covers packing, moving, storage and setup with one accountable team."
        crumbs={[{ label: "Home", href: "/" }, { label: "Services" }]}
        image={HERO_IMAGES.services}
      />

      {/* Brand promise - vision & mission */}
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-12">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {visionMission.slice(0, 2).map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <img
                  src={item.image}
                  alt={`${item.title} - Lahari Packers and Movers Bangalore`}
                  title={`${item.title} - Lahari Packers and Movers Bangalore`}
                  className="h-16 w-16 shrink-0 object-contain"
                />
                <div>
                  <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">{item.title}</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main services */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Core Services"
            title="Pick the service that fits your move"
            subtitle="Each service has its own dedicated page with what is included, how the move works and answers to common questions."
          />

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {serviceDetails.map((service) => (
              <article
                key={service.slug}
                className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
              >
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    title={service.title}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5 md:p-6">
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                    <i className="ri-price-tag-3-line" aria-hidden="true" />
                    {service.eyebrow}
                  </span>
                  <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{service.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{service.intro[0]}</p>
                  <Link
                    to={`/services/${service.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-700"
                  >
                    View full details <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Additional capabilities */}
      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            Additional moving capabilities
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            Services we combine with a move whenever your requirement goes beyond the basics.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {extraFacilities.map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Full capability list */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            Every shifting service we handle
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground-950">
                    {item.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local shifting locality list */}
      {localityAreas.length > 0 && (
        <section className="bg-background-100 py-14 md:py-16">
          <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
            <SectionHeading
              eyebrow="Local Shifting"
              title="Local shifting across Bangalore's residential pockets"
              subtitle="We handle same-city moves between every major residential locality in Bangalore. Pick your area to jump straight to it."
            />
            <div className="mt-10 flex flex-wrap gap-3">
              {localityAreas.map((area) => (
                <Link
                  key={area.name}
                  to={`/services/local-shifting#${areaAnchorId(area.name)}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-700"
                >
                  <i className="ri-map-pin-2-line text-accent-600" aria-hidden="true" />
                  Local Shifting in {area.name}
                </Link>
              ))}
            </div>
            <Link
              to="/services/local-shifting"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-route-line text-lg" aria-hidden="true" /> Explore local shifting service
            </Link>
          </div>
        </section>
      )}

      <CtaBanner />
    </>
  );
}